import { MagnifyingGlassIcon, PlusIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { Button, Card, Input, Spinner } from '@realvisor/ui';
import { useLeads, useDefaultPipeline, type Lead } from '@/hooks';
import { getLeadAgeColor, formatLeadAge } from '@/utils/leadAgeColor';
import { useProject } from '@/contexts/ProjectContext';
import { ProjectTag } from '@/components/common/ProjectBadge';
import CreateLeadModal from '@/components/leads/CreateLeadModal';

function getInitials(firstName: string, lastName?: string | null): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Dnes';
    if (diffDays === 1) return 'Včera';
    if (diffDays < 7) return `Před ${diffDays} dny`;
    if (diffDays < 30) return `Před ${Math.floor(diffDays / 7)} týdny`;
    
    return date.toLocaleDateString('cs-CZ');
  } catch (error) {
    console.error('Invalid date format:', dateString, error);
    return '-';
  }
}

export default function Leads() {
  const navigate = useNavigate();
  const { currentProject, isAllProjects, getProjectColor } = useProject();
  const [search, setSearch] = useState('');
  const [stageId, setStageId] = useState('');
  const [page, setPage] = useState(1);
  const [debouncedSearch] = useDebounce(search, 300);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [hideOwnLeads, setHideOwnLeads] = useState(() => {
    const saved = localStorage.getItem('hideOwnLeads');
    return saved ? JSON.parse(saved) : true; // Defaultně aktivní
  });
  const [statusFilter, setStatusFilter] = useState('active');

  // Vlastní leady (David Choc - contact ID)
  const DAVID_CHOC_CONTACT_ID = 'b291e5c9-f51f-482d-bb0c-3d4a271e465c';

  const { data: pipeline } = useDefaultPipeline();
  // Status i vlastní leady se filtrují na serveru před stránkováním, jinak by
  // stránka po vyřazení spamu zůstala poloprázdná a počty by nesedělo.
  const { data: leadsData, isLoading, error } = useLeads({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    stageId: stageId || undefined,
    projectId: isAllProjects ? undefined : currentProject?.id,
    status: statusFilter,
    excludeContactId: hideOwnLeads ? DAVID_CHOC_CONTACT_ID : undefined,
  });

  const leads = leadsData?.data || [];
  const meta = leadsData?.meta;

  // Seskupit fáze podle stageType pro filtrování napříč pipeline
  const stageTypes = useMemo(() => {
    if (!pipeline?.stages) return [];
    
    // Vytvořit unikátní seznam stage types
    const uniqueTypes = new Map<string, { id: string; name: string }>();
    
    pipeline.stages.forEach(stage => {
      if (stage.stageType && !uniqueTypes.has(stage.stageType)) {
        uniqueTypes.set(stage.stageType, {
          id: stage.stageType, // Použít stageType jako ID pro filtrování
          name: stage.name, // Použít název první fáze s tímto typem
        });
      }
    });
    
    return Array.from(uniqueTypes.values());
  }, [pipeline]);

  const handleRowClick = (lead: Lead) => {
    navigate(`/leads/${lead.id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leady</h1>
          <p className="mt-1 text-sm text-gray-500">
            {meta ? `${meta.total} leadů celkem` : 'Správa všech vašich leadů'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={hideOwnLeads}
              onChange={(e) => {
                const value = e.target.checked;
                setHideOwnLeads(value);
                setPage(1);
                localStorage.setItem('hideOwnLeads', JSON.stringify(value));
              }}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <EyeSlashIcon className="h-4 w-4 text-gray-400" />
            <span>Skrýt vlastní leady</span>
          </label>
          <Button 
            leftIcon={<PlusIcon className="h-5 w-5" />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Nový lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <Input
              placeholder="Hledat leady..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              leftIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
            />
          </div>
          <div className="flex gap-2">
            <select 
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={stageId}
              onChange={(e) => {
                setStageId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Všechny fáze</option>
              {stageTypes.map((stageType) => (
                <option key={stageType.id} value={stageType.id}>{stageType.name}</option>
              ))}
            </select>
            <select
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="active">Aktivní</option>
              <option value="all">Všechny</option>
              <option value="spam">Spam</option>
              <option value="archived">Archivované</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Leads table */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="px-6 py-12 text-center">
            <p className="text-red-500">Chyba při načítání leadů</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500">
              {debouncedSearch || stageId ? 'Žádné leady neodpovídají filtru' : 'Zatím nemáte žádné leady'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Kontakt
                    </th>
                    {isAllProjects && (
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Projekt
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Fáze
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Hodnota
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Zdroj
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Vytvořeno
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {leads.map((lead) => {
                    const ageInfo = getLeadAgeColor(lead.createdAt, lead.lastTouchAt);
                    return (
                    <tr 
                      key={lead.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      style={{ backgroundColor: ageInfo.backgroundColor }}
                      onClick={() => handleRowClick(lead)}
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
                            {getInitials(lead.contact.firstName, lead.contact.lastName)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {lead.contact.firstName} {lead.contact.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {lead.contact.email || lead.contact.phone || 'Bez kontaktu'}
                            </p>
                          </div>
                        </div>
                      </td>
                      {isAllProjects && (
                        <td className="whitespace-nowrap px-6 py-4">
                          <ProjectTag projectId={lead.projectId} />
                        </td>
                      )}
                      <td className="whitespace-nowrap px-6 py-4">
                        <span 
                          className="rounded-full px-2.5 py-0.5 text-xs font-medium"
                          style={{ 
                            backgroundColor: `${lead.currentStage?.color || '#6B7280'}20`, 
                            color: lead.currentStage?.color || '#6B7280' 
                          }}
                        >
                          {lead.currentStage?.name || 'Bez fáze'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {lead.expectedValue ? `${Number(lead.expectedValue).toLocaleString()} Kč` : '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {lead.source?.name || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-sm font-medium"
                            style={{ color: ageInfo.isUrgent ? '#dc2626' : '#6b7280' }}
                          >
                            {formatLeadAge(lead.createdAt)}
                          </span>
                          <span className="text-xs text-gray-400">
                            {ageInfo.label}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta && meta.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
                <p className="text-sm text-gray-500">
                  Stránka {meta.page} z {meta.totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Předchozí
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= meta.totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Další
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create Lead Modal */}
      <CreateLeadModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(leadId) => navigate(`/leads/${leadId}`)}
      />
    </div>
  );
}
